import { Collapse, Divider, Grid, MenuItem } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import getLocale from "../../../../common/helpers/getLocale";
import { getJoblyOccupationsCategories } from "../../jobAdAPI";
import { StyledSelect } from "../../styles";
import {
  ContractType,
  JobAdIntegrationType,
  JobCategory,
  JobExperienceLevel,
  SectionProps,
} from "../../types/common";
import FormGrid from "../FormGrid";
import FormSectionHeader from "../FormSectionHeader";
import PreviewField from "../PreviewField";
import useConditionalOpen from "../useConditionalOpen";

interface OccupationCategory {
  id: string;
  name: string;
}

type JoblySectionProps = SectionProps;

const JoblySection = ({
  isEditing,
  onPublishSelection,
  integrationsToPublish,
  selectedJobAd,
}: JoblySectionProps) => {
  const integrationType = JobAdIntegrationType.Jobly;
  const integrationIsSelectedToBePublished =
    integrationsToPublish?.includes(integrationType);
  const languageCode = getLocale().code;
  const [occupationCategories, setOccupationCategories] = useState<
    OccupationCategory[]
  >([]);

  const [open, handleToggle] = useConditionalOpen(
    true,
    integrationIsSelectedToBePublished,
  );

  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext();

  useEffect(() => {
    getJoblyOccupationsCategories({ language: languageCode }).then(
      (response) => {
        const modifiedResponse = response.map((category) => ({
          id: category.id,
          name: category.name,
        }));
        setOccupationCategories(modifiedResponse);
      },
    );
  }, [languageCode]);

  const sortedOccupationCategories = useMemo(
    () => occupationCategories.sort((a, b) => a.name.localeCompare(b.name)),
    [occupationCategories],
  );

  useEffect(() => {
    trigger();
  }, [integrationIsSelectedToBePublished, open, trigger]);

  return (
    <>
      {(isEditing || integrationIsSelectedToBePublished) && (
        <>
          <FormSectionHeader
            isOpen={open}
            toggleOpen={handleToggle}
            title={"JOBLY"}
            integrationType={integrationType}
            onPublishSelection={onPublishSelection}
            isEditing={isEditing}
            selectedJobAd={selectedJobAd}
            integrationIsSelectedToBePublished={
              integrationIsSelectedToBePublished
            }
          />
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Grid
              container
              alignItems="center"
              columnSpacing={2}
              rowGap={1}
              maxWidth={"1000px"}
            >
              <FormGrid
                title={t("jobAds:jobCategory")}
                contentSize={4}
                required
              >
                <Controller
                  name="jobCategoryJobly"
                  control={control}
                  rules={{
                    required: integrationIsSelectedToBePublished,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {isEditing ? (
                        <StyledSelect
                          value={value}
                          onChange={(event) => onChange(event.target.value)}
                          size="small"
                          error={
                            integrationIsSelectedToBePublished &&
                            !!errors.jobCategoryJobly
                          }
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          {Object.values(JobCategory).map((category) => (
                            <MenuItem key={category} value={category}>
                              {t(`jobAds:${category}`)}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                      ) : (
                        <PreviewField text={t(`jobAds:${value}`)} />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              <FormGrid
                title={t("jobAds:contractType")}
                contentSize={4}
                required
              >
                <Controller
                  name="contractTypeJobly"
                  control={control}
                  rules={{
                    required: integrationIsSelectedToBePublished,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {isEditing ? (
                        <StyledSelect
                          value={value}
                          onChange={(event) => onChange(event.target.value)}
                          size="small"
                          error={
                            integrationIsSelectedToBePublished &&
                            !!errors.contractTypeJobly
                          }
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          {Object.values(ContractType).map((contractType) => (
                            <MenuItem key={contractType} value={contractType}>
                              {t(`jobAds:${contractType}`)}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                      ) : (
                        <PreviewField text={t(`jobAds:${value}`)} />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              <FormGrid
                title={t("jobAds:lineOfBusiness")}
                contentSize={4}
                required
              >
                <Controller
                  name="occupationCategoryJobly"
                  control={control}
                  rules={{
                    required: integrationIsSelectedToBePublished,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {isEditing ? (
                        <StyledSelect
                          value={value}
                          onChange={(event) => onChange(event.target.value)}
                          size="small"
                          error={
                            integrationIsSelectedToBePublished &&
                            !!errors.occupationCategoryJobly
                          }
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          {sortedOccupationCategories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                      ) : (
                        <PreviewField
                          text={
                            sortedOccupationCategories.find(
                              (category) => category.id === value,
                            )?.name || ""
                          }
                        />
                      )}
                    </>
                  )}
                />
              </FormGrid>

              <FormGrid title={t("jobAds:jobExperienceLevel")} contentSize={4}>
                <Controller
                  name="jobExperienceLevelJobly"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {isEditing ? (
                        <StyledSelect
                          value={value}
                          onChange={(event) => onChange(event.target.value)}
                          size="small"
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          {Object.values(JobExperienceLevel)
                            .filter((value) => typeof value === "string")
                            .map((level) => (
                              <MenuItem key={level} value={level}>
                                {t(`jobAds:${level}`)}
                              </MenuItem>
                            ))}
                        </StyledSelect>
                      ) : (
                        <PreviewField text={t(`jobAds:${value}`)} />
                      )}
                    </>
                  )}
                />
              </FormGrid>
            </Grid>
          </Collapse>
          <Divider
            sx={{
              margin: "24px 0 16px 0",
            }}
          />
        </>
      )}
    </>
  );
};

export default JoblySection;
